import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPricing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [windowPricing, setWindowPricing] = useState([]);
  const [gutterPricing, setGutterPricing] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchPricing();
  }, [navigate]);

  const fetchPricing = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/pricing`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWindowPricing(data.filter(item => item.category.includes('Window') || item.category.includes('Front') || item.category.includes('Conservator')));
        setGutterPricing(data.filter(item => item.category.includes('Gutter')));
      }
    } catch (error) {
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrice = (index, field, value, isGutter = false) => {
    if (isGutter) {
      const updated = [...gutterPricing];
      updated[index] = { ...updated[index], [field]: value };
      setGutterPricing(updated);
    } else {
      const updated = [...windowPricing];
      updated[index] = { ...updated[index], [field]: value };
      setWindowPricing(updated);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const allPricing = [...windowPricing, ...gutterPricing];
      
      for (const item of allPricing) {
        await fetch(`${BACKEND_URL}/api/admin/pricing/${item.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(item)
        });
      }
      
      toast.success('Pricing updated successfully!');
    } catch (error) {
      toast.error('Failed to update pricing');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white p-6 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="icon" className="text-white border-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Pricing Management</h1>
              <p className="text-cyan-100">Update service pricing</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-white text-cyan-600 hover:bg-cyan-50">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="container mx-auto p-6 space-y-8">
        {/* Window Cleaning Pricing */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Window Cleaning Prices</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {windowPricing.map((item, index) => (
              <div key={item.id} className="border-b pb-6 last:border-b-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Input
                      value={item.category}
                      onChange={(e) => handleUpdatePrice(index, 'category', e.target.value)}
                      placeholder="e.g., 2-3 Bedroom House"
                    />
                  </div>
                  <div>
                    <Label>Price</Label>
                    <Input
                      value={item.price}
                      onChange={(e) => handleUpdatePrice(index, 'price', e.target.value)}
                      placeholder="e.g., £18"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Description</Label>
                    <Textarea
                      value={item.description}
                      onChange={(e) => handleUpdatePrice(index, 'description', e.target.value)}
                      placeholder="Brief description"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Gutter Cleaning Pricing */}
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Gutter Cleaning Prices</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {gutterPricing.map((item, index) => (
              <div key={item.id} className="border-b pb-6 last:border-b-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Input
                      value={item.category}
                      onChange={(e) => handleUpdatePrice(index, 'category', e.target.value, true)}
                      placeholder="e.g., Standard House"
                    />
                  </div>
                  <div>
                    <Label>Price</Label>
                    <Input
                      value={item.price}
                      onChange={(e) => handleUpdatePrice(index, 'price', e.target.value, true)}
                      placeholder="e.g., £50-£70"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Description</Label>
                    <Textarea
                      value={item.description}
                      onChange={(e) => handleUpdatePrice(index, 'description', e.target.value, true)}
                      placeholder="Brief description"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPricing;
